import {ExperimentsDialog} from '@github-ui/copilot-chat/components/ExperimentsDialog'
import {PromptDialog} from '@github-ui/copilot-chat/components/PromptDialog'
import {useTraceManager} from '@github-ui/copilot-chat/components/tracing/TraceManagerContext'
import {useTraceState} from '@github-ui/copilot-chat/components/tracing/TraceProvider'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useCopilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {shouldHaveTracesAvailable} from '@github-ui/copilot-chat/utils/trace-utils'
import {useSelectedThread} from '@github-ui/copilot-chat/utils/use-selected-thread'
import {isStaff} from '@github-ui/stats'
import {BeakerIcon, LogIcon, NoteIcon, SquareCircleIcon, ToolsIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Dialog} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useMemo, useRef, useState} from 'react'

import {useIsSharedThread} from '../hooks/use-is-shared-thread'
import styles from './StaffToolsMenu.module.css'

export function StaffToolsMenu({inline = false}: {inline?: boolean} = {}) {
  const traceManager = useTraceManager()
  const {traceInfo} = useTraceState()
  const selectedThread = useSelectedThread()
  const isSharedThread = useIsSharedThread()

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [showPromptDialog, setShowPromptDialog] = useState(false)
  const [showExperimentsDialog, setShowExperimentsDialog] = useState(false)
  const promptDialogRef = useRef<HTMLDivElement>(null)
  const experimentsDialogRef = useRef<HTMLDivElement>(null)
  const [tracingRequested, setTracingRequested] = useCopilotLocalStorage<boolean>(
    'getTracingRequestedFlag',
    'setTracingRequestedFlag',
  )

  // Check for traces available for the current thread specifically, including shared threads
  const hasTraceAvailable = useMemo(() => {
    return shouldHaveTracesAvailable(traceInfo?.traces, selectedThread?.id, isSharedThread)
  }, [traceInfo?.traces, selectedThread?.id, isSharedThread])
  const isTraceVisible = Boolean(traceInfo?.visible)

  if (!isStaff() || !copilotFeatureFlags.staffToolsMenu) return null

  const handleShowTraceClick = () => {
    void traceManager.showTraceDisplay(!isTraceVisible)
  }

  const handleRequestTraceClick = () => {
    if (tracingRequested) {
      setTracingRequested(false)
    } else {
      setShowConfirmationDialog(true)
    }
  }

  const handlePromptSettingsClick = () => {
    setShowPromptDialog(true)
  }

  const handleExperimentsClick = () => {
    setShowExperimentsDialog(true)
  }

  return (
    <div className={inline ? styles.inlineContainer : styles.container}>
      <ActionMenu>
        <Tooltip text="Tracing">
          <ActionMenu.Button
            aria-label="Staff Tools"
            className={inline ? styles.inlineButton : undefined}
            icon={ToolsIcon}
            variant={tracingRequested ? 'danger' : undefined}
          />
        </Tooltip>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.Item onSelect={handlePromptSettingsClick}>
              <ActionList.LeadingVisual>
                <NoteIcon />
              </ActionList.LeadingVisual>
              Adjust prompts
            </ActionList.Item>
            <ActionList.Item onSelect={handleExperimentsClick}>
              <ActionList.LeadingVisual>
                <BeakerIcon />
              </ActionList.LeadingVisual>
              Configure experiments
            </ActionList.Item>
            {copilotFeatureFlags.requestTracing && (
              <>
                <ActionList.Item onSelect={handleRequestTraceClick} variant={tracingRequested ? 'danger' : undefined}>
                  <ActionList.LeadingVisual>
                    {tracingRequested ? <SquareCircleIcon /> : <ToolsIcon />}
                  </ActionList.LeadingVisual>
                  {tracingRequested ? 'Stop tracing' : 'Start tracing'}
                </ActionList.Item>
                <ActionList.Item onSelect={handleShowTraceClick} disabled={!hasTraceAvailable}>
                  <ActionList.LeadingVisual>
                    <LogIcon />
                  </ActionList.LeadingVisual>
                  View trace
                </ActionList.Item>
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {showConfirmationDialog && (
        <Dialog
          footerButtons={[
            {buttonType: 'default', content: 'Cancel', onClick: () => setShowConfirmationDialog(false)},
            {
              buttonType: 'primary',
              content: 'Enable tracing',
              onClick: () => {
                setTracingRequested(true)
                setShowConfirmationDialog(false)
              },
            },
          ]}
          onClose={() => setShowConfirmationDialog(false)}
          title="Enable tracing"
        >
          Enabling tracing will capture additional diagnostic information to secure storage. This will include the
          entire contents of the conversation and references used in it.
        </Dialog>
      )}
      {showPromptDialog && (
        <PromptDialog promptDialogRef={promptDialogRef} onDismiss={() => setShowPromptDialog(false)} />
      )}
      {showExperimentsDialog && (
        <ExperimentsDialog
          experimentsDialogRef={experimentsDialogRef}
          onDismiss={() => setShowExperimentsDialog(false)}
        />
      )}
    </div>
  )
}
